/**
 * ventaTicketReplay.ts — Reconstruye la venta original para ticket tras ajustes.
 *
 * Tras devoluciones o anulaciones parciales, el ticket debe mostrar el cobro
 * original y, al pie, cada ajuste con su trazabilidad.
 */

import type { CartItem } from "@/types";
import type { Venta, VentaProducto } from "../types/caja.types";
import { sumAnulacionesMonto } from "./applyAnulacionVenta";
import { ventaProductoKey } from "./devolucionRefundCalculator";
import { ventaProductoListPrice } from "./ventaDiscountReplay";

export function ventaHasPostSaleAdjustments(venta: Venta): boolean {
  return (
    (venta.anulaciones?.length ?? 0) > 0 ||
    (venta.devoluciones?.length ?? 0) > 0 ||
    venta.estado === "anulacion-parcial" ||
    venta.estado === "devolucion-parcial" ||
    venta.estado === "devolucion-total"
  );
}

export function computeVentaTotalOriginal(venta: Venta): number {
  return venta.total + sumAnulacionesMonto(venta);
}

/** Monto cobrado en la venta original (antes de devoluciones y anulaciones). */
export function computeVentaTotalVendido(venta: Venta): number {
  const anulado = sumAnulacionesMonto(venta);
  const devuelto = sumDevolucionesMonto(venta);
  const saldo = Math.max(0, venta.total);

  if (anulado <= 0 && devuelto <= 0) return venta.total;

  if (anulado > 0) return saldo + anulado + devuelto;

  const fromProducts = rebuildOriginalProductosFromVenta(venta).reduce(
    (acc, p) => acc + (p.precioUnitario ?? 0) * (p.cantidad ?? 0),
    0,
  );
  if (fromProducts > 0) {
    if (saldo + devuelto > fromProducts + 1) return fromProducts;
    if (Math.abs(saldo - fromProducts) < 1) return fromProducts;
    return saldo + devuelto;
  }

  if (devuelto > 0 && saldo > 0 && Math.abs(saldo - devuelto) < 1) {
    return devuelto;
  }

  return saldo + devuelto;
}

export function countVentaUnidadesVendidas(venta: Venta): number {
  const rebuilt = rebuildOriginalProductosFromVenta(venta);
  const fromRebuilt = rebuilt.reduce((acc, p) => acc + (p.cantidad ?? 0), 0);
  if (fromRebuilt > 0) return fromRebuilt;

  let fromDevoluciones = 0;
  for (const dev of venta.devoluciones ?? []) {
    for (const item of dev.items) {
      fromDevoluciones += item.cantidad ?? 0;
    }
  }
  if (fromDevoluciones > 0) return fromDevoluciones;

  return (venta.productos ?? []).reduce((acc, p) => acc + (p.cantidad ?? 0), 0);
}

export function getVentaProductosHistorial(venta: Venta): VentaProducto[] {
  const rebuilt = rebuildOriginalProductosFromVenta(venta);
  if (rebuilt.length > 0) return rebuilt;
  return venta.productos ?? [];
}

export function sumDevolucionesMonto(venta: Venta): number {
  return (venta.devoluciones ?? []).reduce(
    (acc, d) => acc + (d.montoReembolsado ?? 0),
    0,
  );
}

/** Saldo activo del registro tras devoluciones o anulaciones parciales. */
export function getVentaSaldoVigente(venta: Venta): number {
  if (venta.estado === "anulada") return 0;

  const anulado = sumAnulacionesMonto(venta);
  const devuelto = sumDevolucionesMonto(venta);

  if (anulado > 0) return Math.max(0, venta.total);
  if (devuelto > 0) return Math.max(0, venta.total - devuelto);
  return Math.max(0, venta.total);
}

export function ventaTieneDevoluciones(venta: Venta): boolean {
  return (venta.devoluciones?.length ?? 0) > 0;
}

export function ventaTieneAnulacionesRegistradas(venta: Venta): boolean {
  return (
    (venta.anulaciones?.length ?? 0) > 0 ||
    venta.estado === "anulada" ||
    venta.estado === "anulacion-parcial"
  );
}

function ventaProductoToCartItem(venta: Venta, p: VentaProducto): CartItem {
  // precioUnitario persistido = precio cobrado real; precioLista = lista si la
  // línea tenía descuento/precio especial (si no, ambos coinciden).
  const lista = ventaProductoListPrice(venta, p);
  return {
    id: p.productoId,
    nombre: p.nombre,
    precio: p.precioUnitario,
    precioLista: lista,
    precioMayorista: p.precioMayorista,
    quantity: p.cantidad,
    esMayorista: p.esMayorista,
    esVIP: p.esVIP,
    vipDescuentoPct: p.vipDescuentoPct,
    tieneIVA: p.tieneIVA,
    porcentajeIVA: p.porcentajeIVA,
    peso: p.peso,
    precioPorKilo: p.precioPorKilo,
    variantesSeleccionadas: p.variantesSeleccionadas,
    variantKey: p.variantKey,
    skuVariante: p.skuVariante,
    modificadoresSeleccionados: p.modificadoresSeleccionados,
    modifierKey: p.modifierKey,
    stock: 0,
    categoria: "",
    estado: "activo",
    businessId: "",
    costo: 0,
    stockMinimo: 0,
    createdAt: "",
    updatedAt: "",
  };
}

function mergeProductoIntoMap(
  map: Map<string, VentaProducto>,
  p: VentaProducto,
): void {
  const key = ventaProductoKey(p);
  const prev = map.get(key);
  if (prev) {
    map.set(key, { ...prev, cantidad: prev.cantidad + p.cantidad });
    return;
  }
  map.set(key, { ...p });
}

/** Productos al momento del cobro (incluye ítems ya anulados). */
export function rebuildOriginalProductosFromVenta(
  venta: Venta,
): VentaProducto[] {
  const lineDetails = venta.descuentos?.lineDetails ?? [];
  const merged = new Map<string, VentaProducto>();

  for (const p of venta.productos) {
    mergeProductoIntoMap(merged, p);
  }

  for (const anu of venta.anulaciones ?? []) {
    for (const item of anu.items) {
      const detail =
        lineDetails.find((d) => d.productoId === item.productoId) ??
        lineDetails.find((d) => d.nombre === item.nombre);
      mergeProductoIntoMap(merged, {
        productoId: item.productoId,
        nombre: item.nombre,
        cantidad: item.cantidad,
        precioUnitario: item.precioUnitario,
        variantKey: detail?.variantKey,
        modifierKey: detail?.modifierKey,
        variantesSeleccionadas: undefined,
        skuVariante: undefined,
        modificadoresSeleccionados: undefined,
      });
    }
  }

  const snapLines = venta.descuentos?.breakdownSnapshot?.lines;
  if (snapLines?.length && merged.size === 0) {
    return snapLines.map((line, index) => {
      const detail =
        lineDetails.find((d) => d.nombre === line.nombre) ?? lineDetails[index];
      const qty = Math.max(1, line.cantidad ?? detail?.cantidad ?? 1);
      const unitOrig =
        line.precioUnitarioOriginal ??
        detail?.precioUnitarioOriginal ??
        (line.bruto > 0 && qty > 0 ? Math.round(line.bruto / qty) : 0);
      return {
        productoId:
          detail?.productoId ?? line.lineId.split("#")[0] ?? line.lineId,
        nombre: line.nombre,
        cantidad: qty,
        precioUnitario: unitOrig,
        variantKey: detail?.variantKey,
        modifierKey: detail?.modifierKey,
      };
    });
  }

  if (snapLines?.length && merged.size > 0) {
    const order = snapLines.map((line, index) => {
      const detail =
        lineDetails.find((d) => d.nombre === line.nombre) ?? lineDetails[index];
      const key = detail
        ? ventaProductoKey({
            productoId: detail.productoId,
            variantKey: detail.variantKey,
            modifierKey: detail.modifierKey,
          })
        : line.lineId;
      return merged.get(key) ?? merged.get(line.lineId.split("#")[0]);
    });
    const ordered = order.filter((p): p is VentaProducto => p != null);
    if (ordered.length > 0) return ordered;
  }

  return [...merged.values()];
}

export function rebuildOriginalCartFromVenta(venta: Venta): CartItem[] {
  return rebuildOriginalProductosFromVenta(venta).map((p) =>
    ventaProductoToCartItem(venta, p),
  );
}
